// Sugalarm — Cycle 76: lightweight, dependency-free confetti celebration.
// Shown after a reminder is saved. Pure CSS-animated pieces + a centered
// card; auto-dismisses after a few seconds or on tap.

import { useEffect, useMemo } from "react";

import styles from "./ConfettiCelebration.module.css";

interface Props {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
}

const COLORS = [
  "#3b82f6",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#a855f7",
  "#06b6d4",
  "#ec4899",
];
const PIECE_COUNT = 80;
const AUTO_DISMISS_MS = 4000;

export function ConfettiCelebration({ open, title, subtitle, onClose }: Props) {
  // Randomize each piece once per open. Math.random() is fine in app
  // code (it's only disallowed inside Workflow scripts).
  const pieces = useMemo(() => {
    if (!open) return [];
    return Array.from({ length: PIECE_COUNT }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 2.4 + Math.random() * 1.8,
      color: COLORS[i % COLORS.length],
      rotate: Math.random() * 720 - 360,
      size: 6 + Math.random() * 7,
      drift: (Math.random() - 0.5) * 160,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(onClose, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className={styles.layer} aria-hidden>
        {pieces.map((p, i) => (
          <span
            key={i}
            className={styles.piece}
            style={{
              left: `${p.left}%`,
              background: p.color,
              width: p.size,
              height: p.size * 0.5,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              ["--drift" as never]: `${p.drift}px`,
              ["--rot" as never]: `${p.rotate}deg`,
            }}
          />
        ))}
      </div>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <span className={styles.check} aria-hidden>
          <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
            <circle cx="17" cy="17" r="15" stroke="currentColor" strokeWidth="2" />
            <path
              d="M10.5 17.5 L15 22 L23.5 12.5"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <div className={styles.title}>{title}</div>
        {subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null}
        <button type="button" className={styles.btn} onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}
